<script setup lang="ts">
/**
 * A group's meals (SPEC §8.12, §11.6).
 *
 * Four views, because an organizer arrives here with one of four questions:
 * what is next, what happened, what am I still writing, and what did we call
 * off. Each gets its own empty state, since "nothing here" means something
 * different in each and a single shrug would be useless in all four.
 */
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ApiError, endpoints, type MealEventSummaryResponse } from '@/api'
import EmptyState from '@/components/EmptyState.vue'
import { asCount } from '@/utils/numbers'

type View = 'upcoming' | 'drafts' | 'completed' | 'cancelled'

const views: ReadonlyArray<{ value: View; label: string; headline: string; empty: string }> = [
  {
    value: 'upcoming',
    label: 'Upcoming',
    headline: 'Nothing on the calendar',
    empty: 'Make a meal and publish it, and your families will see it.',
  },
  {
    value: 'drafts',
    label: 'Drafts',
    headline: 'No drafts',
    empty: 'A draft is a meal nobody has been told about yet.',
  },
  {
    value: 'completed',
    label: 'Completed',
    headline: 'No meals have finished yet',
    empty: 'Meals move here on their own once they are over.',
  },
  {
    value: 'cancelled',
    label: 'Cancelled',
    headline: 'Nothing has been called off',
    empty: 'Cancelled meals stay here rather than disappearing.',
  },
]

const route = useRoute()
const router = useRouter()

const groupId = computed(() => String(route.params.groupId))

const view = ref<View>('upcoming')
const events = ref<MealEventSummaryResponse[]>([])
const page = ref(1)
const total = ref(0)
const pageSize = ref(50)
const hasMore = ref(false)

const loading = ref(true)
const error = ref<string | null>(null)

const creating = ref(false)
const newTitle = ref('')
const newDate = ref('')
const newTime = ref('12:30')
const busy = ref(false)

const emptyMessage = computed(
  () => views.find((v) => v.value === view.value)?.empty ?? 'Nothing here.',
)

/*
  A headline per tab, because "No meals yet" on the Cancelled tab would be a
  lie -- there may be plenty, just none cancelled. The tab is the subject.
*/
const emptyHeadline = computed(
  () => views.find((v) => v.value === view.value)?.headline ?? 'Nothing here',
)

const lastPage = computed(() => Math.max(1, Math.ceil(total.value / pageSize.value)))

function describe(caught: unknown): string {
  return (caught instanceof ApiError && caught.problem?.title) || 'That did not work.'
}

async function load() {
  loading.value = true
  error.value = null

  try {
    const result = await endpoints.listMealEvents(groupId.value, view.value, page.value)

    events.value = result.events
    total.value = asCount(result.total)
    pageSize.value = asCount(result.pageSize, 50)
    hasMore.value = result.hasMore
  } catch (caught) {
    error.value = describe(caught)
  } finally {
    loading.value = false
  }
}

function show(next: View) {
  view.value = next

  // Back to the first page. Staying on page four of a list that has one page
  // would show an empty view that is not empty.
  page.value = 1
}

function open(meal: MealEventSummaryResponse) {
  void router.push({
    name: 'organizer-event',
    params: { groupId: groupId.value, eventId: meal.id },
  })
}

async function create() {
  busy.value = true
  error.value = null

  try {
    const meal = await endpoints.createMealEvent(groupId.value, {
      title: newTitle.value,
      startDate: newDate.value,
      startTime: newTime.value,
    })

    creating.value = false

    // Straight to the menu. A meal created a second ago has nobody invited and
    // nothing on it, so its readiness dashboard would be five zeroes; what the
    // organizer came here to do is write the menu.
    await router.push({
      name: 'organizer-event-menu',
      params: { groupId: groupId.value, eventId: meal.id },
    })
  } catch (caught) {
    error.value = describe(caught)
  } finally {
    busy.value = false
  }
}

function openCreate() {
  newTitle.value = ''
  newDate.value = ''
  newTime.value = '12:30'
  creating.value = true
}

watch([view, page], () => void load())

void load()
</script>

<template>
  <div class="events">
    <header class="events__head">
      <h1 class="events__title">Meals</h1>

      <v-btn color="primary" variant="flat" prepend-icon="mdi-plus" @click="openCreate">
        New meal
      </v-btn>
    </header>

    <v-tabs :model-value="view" class="events__tabs" @update:model-value="show($event as View)">
      <v-tab v-for="tab in views" :key="tab.value" :value="tab.value">{{ tab.label }}</v-tab>
    </v-tabs>

    <v-alert v-if="error" type="error" variant="tonal" class="mb-4">{{ error }}</v-alert>

    <v-skeleton-loader v-if="loading" type="list-item-two-line@3" />

    <EmptyState
      v-else-if="events.length === 0 && !error"
      icon="mdi-silverware-fork-knife"
      :headline="emptyHeadline"
      :description="emptyMessage"
    >
      <template v-if="view === 'upcoming' || view === 'drafts'" #action>
        <v-btn color="primary" variant="flat" prepend-icon="mdi-plus" @click="openCreate">
          New meal
        </v-btn>
      </template>
    </EmptyState>

    <ul v-else class="events__list">
      <li v-for="meal in events" :key="meal.id" class="events__row">
        <button type="button" class="events__open" @click="open(meal)">
          <span class="events__name">
            {{ meal.title }}
            <v-icon
              v-if="meal.fromSeries"
              icon="mdi-repeat"
              size="small"
              aria-label="Part of a series"
              class="events__repeat"
            />
          </span>
          <span class="events__meta">
            {{ meal.localDate }} at {{ meal.localTime }}
            <template v-if="meal.locationName"> · {{ meal.locationName }}</template>
          </span>
        </button>

        <v-chip size="small" variant="tonal">{{ meal.status }}</v-chip>
      </li>
    </ul>

    <nav v-if="lastPage > 1" class="events__pages" aria-label="Pages">
      <v-btn
        :disabled="page === 1 || loading"
        variant="text"
        density="comfortable"
        @click="page -= 1"
      >
        Previous
      </v-btn>
      <span class="events__page-count">Page {{ page }} of {{ lastPage }}</span>
      <v-btn
        :disabled="!hasMore || loading"
        variant="text"
        density="comfortable"
        @click="page += 1"
      >
        Next
      </v-btn>
    </nav>

    <v-dialog :model-value="creating" max-width="28rem" @update:model-value="creating = false">
      <v-card>
        <v-card-title tag="h2">New meal</v-card-title>
        <v-card-text class="events__form">
          <p class="events__form-hint">
            This makes a draft. Nobody is told about it until you publish it.
          </p>
          <v-text-field
            v-model="newTitle"
            label="What is it called"
            placeholder="Sunday Lunch"
            variant="outlined"
            density="comfortable"
            hide-details
          />
          <v-text-field
            v-model="newDate"
            type="date"
            label="Date"
            variant="outlined"
            density="comfortable"
            hide-details
          />
          <v-text-field
            v-model="newTime"
            type="time"
            label="Time"
            variant="outlined"
            density="comfortable"
            hide-details
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="creating = false">Cancel</v-btn>
          <v-btn
            :loading="busy"
            :disabled="newTitle.trim() === '' || newDate === ''"
            variant="flat"
            color="primary"
            @click="create"
          >
            Make the draft
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.events {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.events__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.events__title {
  font-size: 1.5rem;
  margin: 0;
}

.events__empty {
  color: rgb(var(--v-theme-on-surface-variant));
  max-width: 52ch;
}

.events__list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.events__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 1rem;
  border: 1px solid rgb(var(--v-border-color), var(--v-border-opacity));
  border-radius: 0.5rem;
}

.events__open {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.15rem;
  flex: 1 1 auto;
  background: none;
  border: 0;
  padding: 0;
  color: inherit;
  cursor: pointer;
  text-align: left;
}

.events__name {
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.events__open:hover .events__name {
  text-decoration: underline;
}

.events__repeat {
  color: rgb(var(--v-theme-on-surface-variant));
}

.events__meta {
  color: rgb(var(--v-theme-on-surface-variant));
  font-size: 0.875rem;
}

.events__pages {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
}

.events__page-count {
  color: rgb(var(--v-theme-on-surface-variant));
  font-size: 0.875rem;
}

.events__form {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.events__form-hint {
  margin: 0;
  color: rgb(var(--v-theme-on-surface-variant));
}
</style>
